import { useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  FileText, Calendar, Clock, Filter, Car,
  List, LayoutGrid, ChevronDown, ChevronUp
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { workLedgerSourceLabel } from '@/lib/passport/workLedgerProvenance';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { SafeChart } from '@/components/passport/SafeChart';

interface PassportTicketLedgerProps {
  technicianProfileId: string;
}

type ViewMode = 'timeline' | 'table';

/**
 * Core-100 R4 (CORE-J): one entry of the person's work ledger, exactly as `passport_work_ledger` returns it. The
 * ledger is a person-owned, privacy-safe projection (`work_ledger_entries`): no customer, VIN, plate, price, pay,
 * note, other-employee or ticket identifier is ever RETRIEVED, because the server returns none.
 */
export interface WorkLedgerEntry {
  id: string;
  job_name: string | null;
  job_category: string | null;
  vehicle_year: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_engine: string | null;
  actual_minutes: number | null;
  predicted_minutes: number | null;
  comeback_flag: boolean;
  completed_at: string | null;
  shop_name: string | null;
  /** 'imported' = historical work brought in from another system; 'observed' = recorded by OptiBay. */
  source_kind: 'observed' | 'imported';
  /** This entry supersedes an earlier one (a correction); the earlier entry is kept. */
  corrected: boolean;
  /** The shop removed (or closed) its own record of this visit; the person's evidence is kept. */
  shop_record_removed: boolean;
}

type ProcessedJob = WorkLedgerEntry;

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--success))',
  'hsl(var(--warning))',
  'hsl(var(--destructive))',
  'hsl(var(--accent))',
  'hsl(var(--secondary))',
];

// Format category key to display name
const formatCategoryName = (key: string): string => {
  const categoryMap: Record<string, string> = {
    'engine_mechanical': 'Engine Mechanical',
    'engine_performance': 'Engine Performance',
    'transmission': 'Transmission',
    'drivetrain': 'Drivetrain',
    'electrical': 'Electrical',
    'diagnostics': 'Diagnostics',
    'ev_hybrid': 'EV / Hybrid',
    'starting_charging': 'Starting & Charging',
    'adas': 'ADAS',
    'steering_suspension': 'Steering & Suspension',
    'brakes': 'Brakes',
    'tires': 'Tires',
    'alignment': 'Alignment',
    'body_frame': 'Body & Frame',
    'maintenance': 'Maintenance',
    'inspections': 'Inspections',
    'heatingVentilationAC': 'Heating Ventilation A/C',
    'heating_ventilation_ac': 'Heating Ventilation A/C',
    'restraints': 'Restraints',
    'detailing': 'Detailing',
  };
  return categoryMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/** Visits read per page of the ledger (Core-100 R2, CORE-J). */
export const LEDGER_VISIT_PAGE = 100;
interface LedgerRead { jobs: ProcessedJob[]; visitsShown: number; totalVisits: number | null }
const EMPTY_LEDGER: LedgerRead = { jobs: [], visitsShown: 0, totalVisits: 0 };

export function PassportTicketLedger({ technicianProfileId }: PassportTicketLedgerProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [efficiencyFilter, setEfficiencyFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [limit, setLimit] = useState(5);
  // Core-100 R2 (CORE-J): the ledger read the latest 100 visits and said nothing about the rest. It now
  // reads a window of visits, says how many exist, and can read further back.
  const [visitWindow, setVisitWindow] = useState(LEDGER_VISIT_PAGE);

  // Core-100 R4 (CORE-J): the ledger is read through ONE server authority. The browser used to assemble it from
  // `tickets` / `ticket_jobs`, which needed policies granting whole-row reads of former employers' visits (customer,
  // notes, labour amounts) and let a shop's soft delete erase the person's evidence. Both are gone.
  const { data: ledger, isLoading, isError } = useQuery({
    queryKey: ['passport-work-ledger', technicianProfileId, visitWindow],
    queryFn: async (): Promise<LedgerRead> => {
      const { data, error } = await supabase.rpc('passport_work_ledger' as never, {
        p_technician_profile_id: technicianProfileId,
        p_limit: visitWindow,
        p_offset: 0,
      } as never);
      // CORE-C GAP-003: a read that did not happen reaches the component as a failure, never as an empty ledger.
      if (error) throw error;
      const payload = data as unknown as { entries?: WorkLedgerEntry[]; total?: number } | null;
      if (!payload || !Array.isArray(payload.entries)) {
        throw new Error('passport_work_ledger returned no ledger');
      }
      return {
        jobs: payload.entries,
        visitsShown: payload.entries.length,
        totalVisits: typeof payload.total === 'number' ? payload.total : null,
      };
    },
  });
  const ticketJobs = ledger?.jobs;
  const olderVisits = ledger && ledger.totalVisits !== null ? Math.max(0, ledger.totalVisits - ledger.visitsShown) : 0;

  // Calculate job mix for pie chart with formatted names
  const jobMixData = ticketJobs?.reduce((acc, job) => {
    const rawCategory = job.job_category || 'Other';
    const category = formatCategoryName(rawCategory);
    const existing = acc.find(a => a.name === category);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: category, value: 1, rawKey: rawCategory });
    }
    return acc;
  }, [] as { name: string; value: number; rawKey: string }[]) || [];

  // Get unique categories for filter (keep raw keys for filtering, display formatted)
  const categories = [...new Set(ticketJobs?.map(j => j.job_category).filter(Boolean))] as string[];

  // Filter jobs
  const filteredJobs = ticketJobs?.filter(job => {
    if (categoryFilter !== 'all' && job.job_category !== categoryFilter) return false;
    
    if (efficiencyFilter !== 'all') {
      const efficiency = job.actual_minutes && job.predicted_minutes 
        ? (job.predicted_minutes / job.actual_minutes) * 100 
        : 100;
      if (efficiencyFilter === 'fast' && efficiency < 105) return false;
      if (efficiencyFilter === 'slow' && efficiency > 95) return false;
    }
    
    return true;
  }).slice(0, limit) || [];

  // Core-100 R4 (CORE-J Prov): a per-entry figure carries what it is and never implies causation. It is measured
  // minutes against the book time for the job; waits for parts or authorisation are not recorded on the entry, so it
  // is shown neutrally (no red "-30%"), named, and explained.
  const getBookComparison = (actual: number | null, predicted: number | null) => {
    if (!actual || !predicted) return null;
    const delta = Math.round((predicted / actual) * 100 - 100);
    return (
      <Badge
        variant="outline"
        className="text-xs"
        data-testid="ledger-book-comparison"
        title="Measured minutes compared with book time for this job. Context such as parts or authorisation waits is not recorded here, so this is not a judgement of speed."
      >
        Book {predicted} min · measured {actual} min{delta === 0 ? '' : ` (${delta > 0 ? '+' : ''}${delta}% vs book)`}
      </Badge>
    );
  };

  // CORE-C GAP-003: before the loading branch returns, and well before
  // "No tickets found" — a failed read is not an empty work history.
  if (isError) {
    return <PassportSectionFallback sectionName="Ticket Ledger" />;
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            Ticket Ledger
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-1" />
              Filters
              {showFilters ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
            </Button>
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('timeline')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{formatCategoryName(cat)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={efficiencyFilter} onValueChange={setEfficiencyFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Efficiency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="fast">Fast (+5%)</SelectItem>
                <SelectItem value="slow">Slow (-5%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Job Mix Donut Chart */}
        {jobMixData.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="h-[200px]">
              <p className="text-sm font-medium text-muted-foreground mb-2">Job Mix Distribution</p>
              <SafeChart height="200px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={jobMixData}
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {jobMixData.map((_, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.5rem',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </SafeChart>
            </div>
            <div className="flex flex-wrap gap-2 content-start">
              {jobMixData.slice(0, 6).map((item, index) => (
                <Badge 
                  key={item.name}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <div 
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  {item.name}: {item.value}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Ticket List */}
        <div className="space-y-3">
          {filteredJobs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No tickets found</p>
          ) : (
            filteredJobs.map(job => (
              <div 
                key={job.id}
                className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground truncate">
                        {job.job_name || 'Unknown Job'}
                      </span>
                      {job.comeback_flag && (
                        <Badge variant="destructive" className="text-xs">Comeback</Badge>
                      )}
                    </div>
                    {(job.vehicle_year || job.vehicle_make) && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                        <Car className="h-3 w-3" />
                        <span>
                          {[
                            job.vehicle_year,
                            job.vehicle_make,
                            job.vehicle_model,
                            job.vehicle_engine,
                          ].filter(Boolean).join(' ')}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-1">
                      {job.shop_name && <span data-testid="ledger-shop">{job.shop_name}</span>}
                      <Badge variant="outline" className="text-xs" data-testid="ledger-source">
                        {workLedgerSourceLabel(job.source_kind)}
                      </Badge>
                      {job.corrected && (
                        <Badge variant="outline" className="text-xs" data-testid="ledger-corrected">Corrected entry</Badge>
                      )}
                      {job.shop_record_removed && (
                        <span data-testid="ledger-shop-removed">Shop record removed; kept as your evidence</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : 'Date not recorded'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {job.actual_minutes || '?'} min
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {formatCategoryName(job.job_category || 'N/A')}
                    </Badge>
                    {getBookComparison(job.actual_minutes, job.predicted_minutes)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load More */}
        {filteredJobs.length > 0 && ticketJobs && ticketJobs.length > limit && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setLimit(l => l + 10)}
          >
            Load More ({Math.max(0, ticketJobs.length - limit)} remaining)
          </Button>
        )}

        {/* Core-100 R2 (CORE-J): the window of visits is stated, never silently cut. */}
        {olderVisits > 0 && (
          <div className="flex items-center justify-between rounded-md border p-3 text-sm" data-testid="ledger-older-visits">
            <span className="text-muted-foreground">
              Showing the latest {ledger!.visitsShown} of {ledger!.totalVisits} ledger entries.
            </span>
            <Button variant="outline" size="sm" onClick={() => setVisitWindow((w) => w + LEDGER_VISIT_PAGE)}>
              Load older visits
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
